import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ConfigService } from '@ats/service-base';
import { AuthService } from '@auth0/auth0-angular';
import { Subject, takeUntil } from 'rxjs';
import { IAppUser } from '../../../interfaces/user.interface';

@Component({
  selector: 'app-masthead-user',
  templateUrl: './masthead-user.component.html',
  styleUrls: ['./masthead-user.component.scss'],
  standalone: false
})
export class MastheadUserComponent implements OnInit, OnChanges, OnDestroy {
  constructor(
    private configService: ConfigService,
    private authService: AuthService
  ) {}

  /**
   * Logged in user object
   */
  @Input() public person: IAppUser | undefined;
  /**
   * Whether to show the light and dark mode switcher
   */
  @Input() showThemeSwitch: boolean = false;
  /**
   * Whether to show the high contrast option toggle
   */
  @Input() showContrastSwitch: boolean = false;

  /**
   * Whether user may install the IFS client
   */
  mayInstallClient: boolean = false;
  /**
   * client install uri
   */
  clientInstallLink: string = '';
  /**
   * Users initials shown in portal
   */
  userInitials: string = '';
  /**
   * User email - used for theme saving
   */
  userEmail: string = '';
  /**
   * Whether theme checkbox is checked
   */
  isDarkThemeActive: boolean = false;
  /**
   * Whether high contrast mode checkbox is checked
   */
  isHighContrastActive: boolean = false;
  /**
   * Subscription stop subject
   */
  _unsubscribe = new Subject<void>();

  ngOnInit(): void {
    this.clientInstallLink = `${this.configService.cdnUri}/client/setup.exe`;

    this.userReady(this.person);
  }

  ngOnChanges(_changes: SimpleChanges): void {
    this.userReady(this.person);
  }

  ngOnDestroy(): void {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }

  userReady(person: IAppUser | undefined): void {
    if (!person) {
      return;
    }

    this.userInitials = `${person.firstname?.substr(0, 1)}${person.lastname?.substr(0, 1)}`;
    this.userEmail = person.email?.toLowerCase();
    this.isDarkThemeActive = document.documentElement.classList.contains('darkMode');
    this.isHighContrastActive = document.documentElement.classList.contains('highContrast');

    this.mayInstallClient =
      [
        'ADMIN',
        'IT APP SUPPORT',
        'IT OPS SUPPORT',
        'MAINTENANCE SUPPORT',
        'IT SERVICE DESK',
        'FMS TECHNICIAN',
        'SUPERVISOR',
        'SUPERVISOR UK',
        'SERVICES SUPERVISOR',
        'SERVICES SUPPORT',
        'SERVICES TECHNICIAN',
        'SUPPLY CHAIN SUPPORT',
        'SUPPLY CHAIN SPECIALIST',
        'SUPPLY CHAIN UK',
        'SUPPLY CHAIN SPECIALIST MX'
      ].filter((x) => person.roles?.map((r: string) => r?.toUpperCase()).indexOf(x) > -1).length >
      0;
  }

  stopPropagation(event: MouseEvent): void {
    event.stopPropagation();
  }

  toggleTheme(): void {
    if (document.documentElement.classList.contains('darkMode')) {
      localStorage.setItem(`efp-theme-${this.userEmail}`, 'light');
      document.documentElement.classList.remove('darkMode');
      document.documentElement.classList.add('lightMode');
    } else {
      localStorage.setItem(`efp-theme-${this.userEmail}`, 'dark');
      document.documentElement.classList.remove('lightMode');
      document.documentElement.classList.add('darkMode');
    }
  }

  toggleContrast(): void {
    if (document.documentElement.classList.contains('highContrast')) {
      localStorage.setItem(`efp-high-contrast-${this.userEmail}`, 'normal');
      document.documentElement.classList.remove('highContrast');
    } else {
      localStorage.setItem(`efp-high-contrast-${this.userEmail}`, 'high');
      document.documentElement.classList.add('highContrast');
    }
  }

  gotoLink(link: string): void {
    document.getElementById(link)?.click();
  }

  logout() {
    this.authService
      .logout({
        logoutParams: {
          returnTo: `${window.location.protocol}//${window.location.host}`
        }
      })
      .pipe(takeUntil(this._unsubscribe))
      .subscribe();
  }
}
